import { clsx } from 'clsx';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

type Variant = 'primary' | 'secondary' | 'danger' | 'text';
type Size = 'sm' | 'md' | 'lg';

const BASE =
  'inline-flex items-center justify-center gap-2 rounded-lg font-semibold ' +
  'transition-all duration-fast disabled:opacity-50 disabled:cursor-not-allowed ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-primary';

const VARIANT: Record<Variant, string> = {
  primary: 'bg-accent text-accent-text hover:opacity-90 focus-visible:ring-[color:var(--focus-ring)]',
  secondary:
    'bg-transparent text-fg-primary border border-line hover:border-line hover:bg-fill-2 focus-visible:ring-[color:var(--focus-ring)]',
  danger:
    'bg-transparent text-danger border border-danger-border hover:bg-danger-bg focus-visible:ring-danger/30',
  text: 'bg-transparent text-fg-secondary hover:text-fg-primary hover:bg-fill-2',
};

const SIZE: Record<Size, string> = {
  sm: 'h-7 px-3 text-xs',
  md: 'h-9 px-4 text-sm',
  lg: 'h-11 px-6 text-md',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  children: ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading,
  disabled,
  className,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={clsx(BASE, VARIANT[variant], SIZE[size], className)}
    >
      {loading ? <span className="inline-block animate-pulse">…</span> : children}
    </button>
  );
}
